import type { Query, QueryClient } from "@tanstack/react-query";
import { myWorkFeedPrefix } from "@/queries/my-work";
import {
  REQUEST_DEFINITION_PART,
  REQUEST_DOMAIN,
  REQUEST_REVIEWS_DOMAIN,
  REQUESTS_DOMAIN,
} from "@/queries/requests";
import { scopedKey, type QueryScope } from "@/queries/scope";
import { observedRead, type THintTarget, type THintTargets } from "./types";

/** The requests table's pages and the board's columns, every filter and sort. */
const lists = observedRead((scope) => scopedKey(scope, REQUESTS_DOMAIN));
/** The caller's "awaiting my review" queue. */
const reviews = observedRead((scope) => scopedKey(scope, REQUEST_REVIEWS_DOMAIN));
/** My Work's feed, which holds the caller's requests beside their work items. */
const myWorkFeed = observedRead(myWorkFeedPrefix);

const LIST_READS = [lists, reviews, myWorkFeed];

/**
 * The key ids whose request a hint names. A request's reads are keyed by the id
 * its page was opened with, so a cached detail whose record carries a named id
 * counts too, whatever its key says.
 */
function namedKeyIds(
  client: QueryClient,
  scope: QueryScope,
  ids: ReadonlySet<string>,
): Set<string> {
  const keyIds = new Set(ids);
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: scopedKey(scope, REQUEST_DOMAIN) })) {
    if (query.queryKey.length !== 4) continue;
    const record = query.state.data as { id?: unknown } | undefined;
    if (record && ids.has(String(record.id))) keyIds.add(String(query.queryKey[3]));
  }
  return keyIds;
}

/**
 * The reads a request's page holds, for the requests `ids` names (every request
 * when it names none): the detail and its last-seen copy, the workflow stage,
 * the activity and comments, the linked and linkable projects, the priority
 * breakdown, the answer choices and the content suggestions. Not the render
 * definition (`REQUEST_DEFINITION_PART`), the request's pinned snapshot.
 */
function requestReads(
  client: QueryClient,
  scope: QueryScope,
  ids: ReadonlySet<string> | undefined,
) {
  const keyIds = ids ? namedKeyIds(client, scope, ids) : undefined;
  return (query: Query): boolean => {
    const [tenant, principal, domain, id, part] = query.queryKey;
    if (tenant !== scope.tenant || principal !== scope.principal || domain !== REQUEST_DOMAIN)
      return false;
    if (part === REQUEST_DEFINITION_PART) return false;
    return keyIds === undefined || keyIds.has(String(id));
  };
}

/**
 * `requests:item`: the requests changed, so their page's reads are marked stale
 * and the ones on screen re-read. An open editor keeps what is typed in it: the
 * answers form and a pending Page are SurveyJS models seeded once at mount and
 * never re-seeded from a re-read (`IntakeRenderer`), the title seeds its draft
 * when editing starts, and the comment box holds its own draft.
 *
 * Not re-read for this tab's own write (NG-3587): every write the page makes
 * refreshes what the page shows. The row writers patch the detail and rows
 * (`writeRequestRow`) and mark the history stale, a Page submit, a step
 * completion and a decision put or re-read the stage and the detail, the
 * priority breakdown is read per `updated_at`, and comments, links and content
 * suggestions refresh through their own hooks. The history shows only in its
 * own sheet, and nothing on the page writes while that sheet is open.
 */
const records: THintTarget = {
  mark: (_root, _slug, { client, scope }, ids) =>
    void client.invalidateQueries({
      predicate: requestReads(client, scope, ids),
      refetchType: "none",
    }),
  reread: (_root, _slug, { client, scope }, ids) =>
    void client.refetchQueries({
      predicate: requestReads(client, scope, ids),
      type: "active",
      stale: true,
    }),
  skipOwnReread: true,
};

/**
 * The requests domain's hint rows (NG-3589). A request write sends both: `list`
 * for the lists that show it, `item` naming the requests. A comment or an
 * activity row sends `item` alone, since no list shows either; a CSV import
 * sends `list` alone, since nobody has a request it just created open.
 */
export const REQUESTS_HINTS: THintTargets = {
  // The table, the board, the reviewer queue and My Work: which rows each holds,
  // and their values, are the server's answer.
  "requests:list": {
    mark: (root, slug, cache) => {
      for (const read of LIST_READS) read.mark(root, slug, cache);
    },
    reread: (root, slug, cache) => {
      for (const read of LIST_READS) read.reread(root, slug, cache);
    },
  },
  "requests:item": records,
};
